import { useState, useEffect } from 'react';
import { Package, DollarSign, FlaskConical, Rocket, Server } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import MetricCard from '../MetricCard';

interface ProductStatsProps {
  totalProducts: number;
  activeProducts: number;
  devProducts: number;
  totalProjects: number;
}

export default function ProductStats({ totalProducts, activeProducts, devProducts, totalProjects }: ProductStatsProps) {
  const { token } = useAuth();
  const [data, setData] = useState<any>({ finance: [], deploys: [] });

  useEffect(() => {
    if (!token) return;
    Promise.all([
      fetch('/api/finance', { headers: { Authorization: `Bearer ${token}` } }),
      fetch('/api/deploys', { headers: { Authorization: `Bearer ${token}` } })
    ])
    .then(responses => Promise.all(responses.map(r => r.ok ? r.json() : [])))
    .then(([fins, deps]) => {
      setData({ finance: fins, deploys: deps });
    })
    .catch(err => console.error(err));
  }, [token]);

  const revenue = Array.isArray(data.finance) ? data.finance.filter((f: any) => f.type === 'RECEITA' && f.status === 'PAGO').reduce((acc: number, cur: any) => acc + parseFloat(cur.amount || '0'), 0) : 0;
  const deployCount = Array.isArray(data.deploys) ? data.deploys.length : 0;

  const formatCurrency = (val: number) => {
    if (val >= 1000000) return `R$ ${(val / 1000000).toFixed(1)}M`;
    if (val >= 1000) return `R$ ${(val / 1000).toFixed(1)}k`;
    return `R$ ${val.toLocaleString('pt-BR', { minimumFractionDigits: 0 })}`;
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
      <MetricCard 
        title="Total de Produtos"
        value={totalProducts}
        trend="+2 ativos"
        trendUp={true}
        subtext="Produtos no portfólio"
        icon={Package}
        color="text-[#0F172A]"
        bg="bg-slate-100/80"
        secondaryLabel="Em produção"
        secondaryValue={`${activeProducts} de ${totalProducts}`}
        progress={totalProducts > 0 ? Math.round((activeProducts / totalProducts) * 100) : 100}
        progressColor="bg-[#0F172A]"
      />
      <MetricCard 
        title="Receita Consolidada"
        value={formatCurrency(revenue)}
        trend="+18%"
        trendUp={true}
        subtext="Faturamento de produtos"
        icon={DollarSign}
        color="text-emerald-700"
        bg="bg-emerald-50/80"
        secondaryLabel="Meta do Quarter"
        secondaryValue="88%"
        progress={88}
        progressColor="bg-emerald-600"
      />
      <MetricCard 
        title="Em Desenvolvimento"
        value={devProducts}
        trend="Em teste"
        trendUp={true}
        subtext="Próximos do lançamento"
        icon={FlaskConical}
        color="text-[#0F172A]"
        bg="bg-slate-100/80"
        secondaryLabel="Sprint de Dev"
        secondaryValue="72%"
        progress={72}
        progressColor="bg-[#0F172A]"
      />
      <MetricCard 
        title="Projetos Vinculados"
        value={totalProjects}
        trend="Operacional"
        trendUp={true}
        subtext="Iniciativas por produto"
        icon={Rocket}
        color="text-[#0F172A]"
        bg="bg-slate-100/80"
        secondaryLabel="Velocidade"
        secondaryValue="94%"
        progress={94}
        progressColor="bg-[#0F172A]"
      />
      <MetricCard 
        title="Deploys & Releases"
        value={deployCount || 12}
        trend="SLA OK"
        trendUp={true}
        subtext="Builds e deploys estáveis"
        icon={Server}
        color="text-[#0F172A]"
        bg="bg-slate-100/80"
        secondaryLabel="Uptime Global"
        secondaryValue="99.9%"
        progress={99}
        progressColor="bg-[#0F172A]"
      />
    </div>
  );
}

